/**
 * A live connection to the shop server.
 *
 * The Inbox only showed new messages after a reload, which in a shop means a
 * customer's order waits until somebody thinks to refresh. This holds one
 * connection open and reacts the moment the server says something changed.
 *
 * Read with `fetch` rather than `EventSource`, deliberately. `EventSource`
 * cannot set headers, so the session token would have to travel in the query
 * string — and a token in a URL ends up in access logs, which is a leak this
 * project has already had to clean up once. `fetch` sends it in a header.
 */

import { apiBase, authToken } from './api.js';

export interface LiveEvent {
  type: string;
  [key: string]: unknown;
}

/** Backoff between reconnection attempts. Gentle, then patient. */
const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

/**
 * Stay connected and call `onEvent` for each event.
 *
 * Returns a function that stops trying. Never throws: the Inbox is expected to
 * keep working from cache and manual refresh if this cannot connect, so a
 * failure here is a missing convenience, not a broken screen.
 */
export function connectLive(onEvent: (event: LiveEvent) => void): () => void {
  let stopped = false;
  let retry = FIRST_RETRY_MS;
  let controller: AbortController | null = null;

  async function run(): Promise<void> {
    while (!stopped) {
      controller = new AbortController();
      try {
        const token = authToken();
        const response = await fetch(`${apiBase()}/events`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });

        if (!response.ok || !response.body) throw new Error(`stream refused: ${response.status}`);

        // Connected, so the next failure starts its backoff from the bottom.
        retry = FIRST_RETRY_MS;

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += value;

          /*
           * Frames are separated by a blank line and can arrive split across
           * reads, so anything after the last separator stays in the buffer
           * until the rest of it turns up.
           */
          let separator = buffer.indexOf('\n\n');
          while (separator !== -1) {
            const frame = buffer.slice(0, separator);
            buffer = buffer.slice(separator + 2);
            const data = frame
              .split('\n')
              .filter((line) => line.startsWith('data:'))
              .map((line) => line.slice(5).trim())
              .join('');
            // Heartbeats are comment lines with no data; nothing to report.
            if (data) {
              try {
                onEvent(JSON.parse(data) as LiveEvent);
              } catch {
                /* a malformed frame is not worth dropping the connection over */
              }
            }
            separator = buffer.indexOf('\n\n');
          }
        }
      } catch {
        /* falls through to the wait below */
      }

      if (stopped) return;
      await new Promise((resolve) => setTimeout(resolve, retry));
      retry = Math.min(retry * 2, MAX_RETRY_MS);
    }
  }

  void run();

  return () => {
    stopped = true;
    controller?.abort();
  };
}

// --- The sound -------------------------------------------------------------

/*
 * Synthesised rather than a file.
 *
 * A two-note chime is a few lines of Web Audio and needs no asset, which means
 * it works on a device that has never been online and cannot 404. It also
 * cannot be blocked as third-party content.
 */
let audio: AudioContext | null = null;

function context(): AudioContext | null {
  try {
    audio ??= new AudioContext();
    return audio;
  } catch {
    return null;
  }
}

/**
 * Browsers refuse to play audio until the person has interacted with the page,
 * so the context is created suspended and resumed on the first tap or key.
 * Called once at startup; harmless if it never fires.
 */
export function primeNotificationSound(): void {
  const resume = () => {
    void context()?.resume();
    window.removeEventListener('pointerdown', resume);
    window.removeEventListener('keydown', resume);
  };
  window.addEventListener('pointerdown', resume, { once: true });
  window.addEventListener('keydown', resume, { once: true });
}

/**
 * Two rising notes — noticeable across a counter without being an alarm.
 * Silently does nothing if audio is unavailable or still blocked.
 */
export function playNotification(): void {
  const ctx = context();
  if (!ctx || ctx.state === 'suspended') return;

  const now = ctx.currentTime;
  for (const [index, frequency] of [880, 1320].entries()) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;

    const start = now + index * 0.14;
    // A quick fade in and out; an abrupt stop clicks.
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + 0.24);
  }
}
